/**
 * Half-open time interval `[start, end)` in seconds on the *source* timeline.
 * Every function here is pure and treats its inputs as immutable.
 */
export interface TimeRange {
  readonly start: number;
  readonly end: number;
}

/**
 * A kept stretch of the edit, played `rate` times as fast (1 when absent): a
 * sped-up part takes `duration / rate` seconds of the finished video.
 */
export interface PlayRange extends TimeRange {
  readonly rate?: number;
}

export const rateOf = (r: PlayRange): number => r.rate ?? 1;

/** Seconds a kept stretch lasts in the finished video. */
const outputSpan = (r: PlayRange): number => rangeDuration(r) / rateOf(r);

/** Tolerance for floating point comparisons of timestamps (1 µs). */
export const TIME_EPSILON = 1e-6;

export const range = (start: number, end: number): TimeRange => ({ start, end });

export const rangeDuration = (r: TimeRange): number => Math.max(0, r.end - r.start);

export const totalDuration = (ranges: readonly TimeRange[]): number =>
  ranges.reduce((sum, r) => sum + rangeDuration(r), 0);

const isFiniteRange = (r: TimeRange): boolean =>
  Number.isFinite(r.start) && Number.isFinite(r.end) && r.end - r.start > TIME_EPSILON;

/** Sorts, drops empty/invalid ranges and merges overlapping or touching ones. */
export function normalizeRanges(ranges: readonly TimeRange[]): TimeRange[] {
  const sorted = ranges.filter(isFiniteRange).sort((a, b) => a.start - b.start);
  const merged: TimeRange[] = [];
  for (const current of sorted) {
    const last = merged.at(-1);
    if (last && current.start <= last.end + TIME_EPSILON) {
      merged[merged.length - 1] = range(last.start, Math.max(last.end, current.end));
    } else {
      merged.push(range(current.start, current.end));
    }
  }
  return merged;
}

export function clampRange(r: TimeRange, bounds: TimeRange): TimeRange | null {
  const clamped = range(Math.max(r.start, bounds.start), Math.min(r.end, bounds.end));
  return isFiniteRange(clamped) ? clamped : null;
}

/** Returns the parts of `base` that are not covered by any range in `cuts`. */
export function subtractRanges(base: readonly TimeRange[], cuts: readonly TimeRange[]): TimeRange[] {
  const normalizedCuts = normalizeRanges(cuts);
  const result: TimeRange[] = [];
  for (const segment of normalizeRanges(base)) {
    let cursor = segment.start;
    for (const cut of normalizedCuts) {
      if (cut.end <= cursor) continue;
      if (cut.start >= segment.end) break;
      if (cut.start > cursor) result.push(range(cursor, cut.start));
      cursor = Math.max(cursor, cut.end);
      if (cursor >= segment.end) break;
    }
    if (segment.end - cursor > TIME_EPSILON) result.push(range(cursor, segment.end));
  }
  return normalizeRanges(result);
}

/** Complement of `ranges` within `bounds`. */
export const invertRanges = (ranges: readonly TimeRange[], bounds: TimeRange): TimeRange[] =>
  subtractRanges([bounds], ranges);

export function intersectRanges(a: readonly TimeRange[], b: readonly TimeRange[]): TimeRange[] {
  const left = normalizeRanges(a);
  const right = normalizeRanges(b);
  const result: TimeRange[] = [];
  let i = 0;
  let j = 0;
  while (i < left.length && j < right.length) {
    const l = left[i]!;
    const r = right[j]!;
    const start = Math.max(l.start, r.start);
    const end = Math.min(l.end, r.end);
    if (end - start > TIME_EPSILON) result.push(range(start, end));
    if (l.end < r.end) i++;
    else j++;
  }
  return result;
}

/** Removes ranges shorter than `minDuration` (e.g. single-frame slivers left after cutting). */
export const dropShortRanges = (ranges: readonly TimeRange[], minDuration: number): TimeRange[] =>
  ranges.filter((r) => rangeDuration(r) >= minDuration);

/** Length of the finished video made of `keep` (sped-up parts count shorter). */
export const outputLength = (keep: readonly PlayRange[]): number => keep.reduce((sum, r) => sum + outputSpan(r), 0);

/**
 * Maps a source timestamp into the edited (output) timeline built by concatenating `keep`.
 * Returns `null` when the timestamp falls into a removed part.
 */
export function sourceToOutputTime(keep: readonly PlayRange[], t: number): number | null {
  let offset = 0;
  for (const r of keep) {
    if (t < r.start - TIME_EPSILON) return null;
    if (t < r.end) return offset + Math.max(0, t - r.start) / rateOf(r);
    offset += outputSpan(r);
  }
  return null;
}

/** Inverse of {@link sourceToOutputTime}; clamps to the edited timeline's bounds. */
export function outputToSourceTime(keep: readonly PlayRange[], t: number): number {
  if (keep.length === 0) return 0;
  let remaining = Math.max(0, t);
  for (const r of keep) {
    const d = outputSpan(r);
    if (remaining < d) return r.start + remaining * rateOf(r);
    remaining -= d;
  }
  return keep.at(-1)!.end;
}

/** How long the kept part of `span` lasts in the finished video. */
export function outputLengthWithin(keep: readonly PlayRange[], span: TimeRange): number {
  let length = 0;
  for (const r of keep) {
    const overlap = Math.min(r.end, span.end) - Math.max(r.start, span.start);
    if (overlap > TIME_EPSILON) length += overlap / rateOf(r);
  }
  return length;
}

/** How fast the edit plays at source time `t` (1 outside kept parts). */
export function playRateAt(keep: readonly PlayRange[], t: number): number {
  for (const r of keep) {
    if (t < r.start - TIME_EPSILON) return 1;
    if (t < r.end) return rateOf(r);
  }
  return 1;
}

/**
 * Snaps a source timestamp to the nearest kept moment. Used by the player so that
 * playback skips removed parts instead of showing them.
 */
export function nextKeptTime(keep: readonly TimeRange[], t: number): number | null {
  for (const r of keep) {
    if (t < r.end - TIME_EPSILON) return Math.max(t, r.start);
  }
  return null;
}

/**
 * Source ranges as they land on the edited (output) timeline: each part that is
 * kept becomes the matching output stretch. Removed parts disappear, and parts
 * that end up touching merge.
 */
export function toOutputRanges(keep: readonly PlayRange[], ranges: readonly TimeRange[]): TimeRange[] {
  const result: TimeRange[] = [];
  const wanted = normalizeRanges(ranges);
  for (const kept of keep) {
    for (const r of wanted) {
      const from = Math.max(kept.start, r.start);
      const to = Math.min(kept.end, r.end);
      if (to - from <= TIME_EPSILON) continue;
      const start = sourceToOutputTime(keep, from);
      if (start !== null) result.push(range(start, start + (to - from) / rateOf(kept)));
    }
  }
  return normalizeRanges(result);
}
